import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { pageSeo, faqPageJsonLdScript } from "@/lib/seo";
import { breadcrumbJsonLdScript } from "@/lib/breadcrumbs";
import { PATH, TITLE, DESCRIPTION, FAQS } from "@/content/solar-grants-wales";


export const Route = createFileRoute("/solar-grants-wales")({
  head: () => ({
    ...pageSeo({
      path: PATH,
      title: TITLE,
      description: DESCRIPTION,
      ogType: "article",
    }),
    scripts: [
      faqPageJsonLdScript(FAQS),
      breadcrumbJsonLdScript([
        { label: "Home", path: "/" },
        { label: "Solar PV", path: "/solar-pv" },
        { label: "Solar Grants Wales", path: PATH },
      ]),
    ],
  }),
  component: SolarGrantsWalesPage,
});

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">{heading}</h2>
      <div className="text-base leading-relaxed text-foreground/90 space-y-4">{children}</div>
    </section>
  );
}

function SolarGrantsWalesPage() {
  return (
    <>
      <main className="container-page max-w-3xl py-12 md:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-accent">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/solar-pv" className="hover:text-accent">Solar PV</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Solar Grants Wales</span>
        </nav>

        <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-3">
          Solar Panel Grants &amp; Funding In Wales (2026)
        </h1>
        <p className="text-sm text-muted-foreground mb-6">Last updated: July 2026</p>

        <p className="text-lg leading-relaxed text-foreground/90">
          There is no universal solar panel grant in Wales - support is targeted. In 2026, help comes through four routes: 0% VAT on solar and battery installations until 31 March 2027, the Smart Export Guarantee, the Welsh Government&apos;s Warm Homes Nest scheme for eligible households, and ECO4, which ends on 31 December 2026. Interest-free loans are also available through Green Homes Wales.
        </p>

        <Section heading="0% VAT On Solar Panels: Automatic For Everyone">
          <p>
            Every solar panel and battery storage installation in Wales currently has 0% VAT applied to the supply and installation. There is nothing to apply for - your installer applies it automatically. The 0% rate is currently confirmed until 31 March 2027. On a typical £6,000 to £8,000 installation, that is a saving of £1,200 to £1,600 compared with the standard 20% rate.
          </p>
        </Section>

        <Section heading="The Smart Export Guarantee: Get Paid For What You Export">
          <p>
            The Smart Export Guarantee (SEG) is not a grant - it pays you for surplus electricity you export to the grid. You apply for an SEG tariff with a licensed energy supplier using your MCS certificate, and rates and terms are set by the supplier, not by your installer. Every CRS Energy installation comes with the MCS certificate you need to apply.
          </p>
        </Section>

        <Section heading="Warm Homes Nest: Free Improvements For Eligible Households">
          <p>
            Nest is the Welsh Government&apos;s home energy scheme. For eligible households it can fund a package of free improvements that may include solar panels, insulation and heating. To qualify you generally need to own or privately rent your home, receive a means-tested benefit or live in a low-income household, and usually live in a property with a low energy rating. Call Nest free on 0808 808 2244 (Monday to Friday, 9am to 6pm - calls are welcomed in Welsh) or visit gov.wales/nest.
          </p>
        </Section>

        <Section heading="ECO4: CRS No Longer Installs Under This Scheme">
          <p>
            CRS Energy no longer installs under ECO4 or assists with ECO4 eligibility or applications. We are an approved installer under the Green Homes Plan. If your home already has a retrofit assessment, we can carry out the recommended installation work, including solar PV where specified. Eligibility and funding are determined by the scheme and your retrofit assessment, not by CRS. We do not assess eligibility or arrange funding.
          </p>
        </Section>

        <Section heading="Green Homes Wales: Interest-Free Loans Up To £25,000">
          <p>
            Green Homes Wales is a Welsh Government-backed scheme managed by the Development Bank of Wales. It offers homeowners interest-free loans from £1,000 to £25,000 over terms of up to 10 years, with a six-month repayment holiday, for improvements including solar panels, battery storage and insulation. The property must be your main residence, applications are credit-checked, and funding is limited each year. Details and applications are through the Development Bank of Wales.
          </p>
        </Section>

        <Section heading="What About The Warm Homes Plan?">
          <p>
            The UK Government has announced the Warm Homes Plan, a programme of around £15 billion intended to replace ECO4 after December 2026. Eligibility rules and start dates had not been confirmed at the time of writing, so it is not yet possible to apply. We will update this page as details are published.
          </p>
        </Section>

        <Section heading="If You Don't Qualify For A Grant">
          <p>
            Most working households in Wales will not qualify for Nest or ECO4. If that is you, solar can still stack up: 0% VAT applies automatically, the Smart Export Guarantee pays for what you export, and our fixed-price package - 6 panels, a hybrid inverter and a 7.2kWh battery for £5,999, subject to suitability - gives you a clear starting point. You will get a personalised estimate on your first call, with no home visit before you are ready.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link
              to="/solar-pv"
              hash="bottom-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full sm:w-auto"
            >
              Get Your Free Solar Estimate
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <Link
              to="/financing"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-background px-6 py-3.5 font-semibold text-foreground hover:bg-secondary/60 transition-colors w-full sm:w-auto"
            >
              See Financing Options
            </Link>
          </div>
        </Section>

        <section className="mt-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {FAQS.map((f) => (
              <div key={f.question} className="border-b border-border pb-6">
                <h3 className="text-lg font-semibold text-foreground mb-2">{f.question}</h3>
                <p className="text-base leading-relaxed text-foreground/90">{f.answer}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </>
  );
}
